import React from 'react';
import styled from 'styled-components';
import useResizeAware from 'react-resize-aware';
import Spin from '../Spin';
import PDFView from './PDFView';
import ImageView from './ImageView';
import { FileViewerProps, FileType } from './types';

export const FileViewer = <T extends FileType>({
  files,
  firstPageOnly,
  render,
  hideSpin,
}: FileViewerProps<T>) => {
  const [resizeListener, sizes] = useResizeAware();
  const [filesLoaded, setFilesLoaded] = React.useState<boolean[]>([]);

  const onLoad = (idx: number) => () => {
      setFilesLoaded(prev => prev.map((bool, j) => {
          if (j === idx) return true;
          return bool;
        }));
    };

  const fullyLoaded = React.useMemo(() => {
    if (!filesLoaded?.length) return false;

    return filesLoaded.every(Boolean);
  }, [filesLoaded, onLoad]);

  React.useEffect(() => {
    setFilesLoaded(prev => {
      let newLoaded: boolean[];
      if (prev.length < files.length) {
        newLoaded = [...prev];
        for (let i = prev.length; i < files.length; i++) {
          newLoaded.push(false);
        }
      } else if (prev.length > files.length) {
        newLoaded = [];
        for (let i = 0; i < files.length; i++) {
          newLoaded.push(prev[i]);
        }
      } else return prev;

      return newLoaded;
    });
  }, [files]);

  return (
    <>
      <StyledFileViewer>
        {!hideSpin && (
          <StyledSpin center size="large" spinning={!fullyLoaded} />
        )}
        <StyledFileGrid $hidden={!fullyLoaded}>
          {resizeListener}
          {files.filter(Boolean).map((file, i) => {
            if (file.fileType === 'application/pdf') {
              return (
                <PDFView
                  key={i}
                  file={file}
                  render={render}
                  firstPageOnly={firstPageOnly}
                  width={sizes.width}
                  onLoad={onLoad(i)}
                />
              );
            }
            if (
              ['image/jpg', 'image/jpeg', 'image/png'].includes(file.fileType)
            ) {
              return (
                <ImageView
                  key={i}
                  file={file}
                  render={render}
                  onLoad={onLoad(i)}
                />
              );
            }
            window.location.href = file.url;
            return null;
          })}
        </StyledFileGrid>
      </StyledFileViewer>
    </>
  );
};

const StyledFileViewer = styled.div`
  position: relative;
`;

const StyledSpin = styled(Spin)`
  height: calc(100vh - 300px);
  position: absolute;
`;

const StyledFileGrid = styled.div<{ $hidden: boolean }>`
  display: grid;
  grid-template-columns: 1fr;
  grid-row-gap: 25px;
  position: relative;
  opacity ${({ $hidden }) => ($hidden ? 0 : 1)};
  transition: opacity 0.6s;
`;

export default FileViewer;
